import { z } from 'zod';

// Message role enum - matches Prisma ChatMessageRole
export const chatMessageRoleSchema = z.enum(['USER', 'ASSISTANT']);
export type ChatMessageRole = z.infer<typeof chatMessageRoleSchema>;

// Send message request
export const sendMessageSchema = z.object({
  message: z.string().min(1, 'Message cannot be empty').max(2000, 'Message too long'),
  chat_id: z.uuid().optional(),
});
export type SendMessageDto = z.infer<typeof sendMessageSchema>;

export const toolResultSchema = z.object({
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z.string().optional(),
});
export type ToolResult = z.infer<typeof toolResultSchema>;

// Tool call info for storing tool execution details
export const toolCallInfoSchema = z.object({
  tool_name: z.string(),
  arguments: z.record(z.string(), z.unknown()),
  result: toolResultSchema,
  formatted_text: z.string().optional(),
});
export type ToolCallInfo = z.infer<typeof toolCallInfoSchema>;

// Chat message response
export const chatMessageResponseSchema = z.object({
  chat_id: z.uuid(),
  message: z.string(),
  tool_calls_executed: z.array(z.string()),
  timestamp: z.iso.datetime(),
});
export type ChatMessageResponse = z.infer<typeof chatMessageResponseSchema>;

// Chat response (for chat creation)
export const chatResponseSchema = z.object({
  chat_id: z.uuid(),
  created_at: z.iso.datetime(),
});
export type ChatResponse = z.infer<typeof chatResponseSchema>;

// Chat message item (for history)
export const chatMessageItemSchema = z.object({
  id: z.uuid(),
  role: chatMessageRoleSchema,
  content: z.string(),
  tool_calls: z.array(toolCallInfoSchema).nullable().optional(),
  created_at: z.iso.datetime(),
});
export type ChatMessageItem = z.infer<typeof chatMessageItemSchema>;

// Chat messages response
export const chatMessagesResponseSchema = z.object({
  chat_id: z.uuid(),
  messages: z.array(chatMessageItemSchema),
  total_messages: z.number().int(),
});
export type ChatMessagesResponse = z.infer<typeof chatMessagesResponseSchema>;

// Delete chat response
export const deleteChatResponseSchema = z.object({
  message: z.string(),
  deleted_messages: z.number().int(),
});
export type DeleteChatResponse = z.infer<typeof deleteChatResponseSchema>;

// Chat list item
export const chatListItemSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type ChatListItem = z.infer<typeof chatListItemSchema>;

// Chat list response
export const chatListResponseSchema = z.object({
  chats: z.array(chatListItemSchema),
  total: z.number().int(),
});
export type ChatListResponse = z.infer<typeof chatListResponseSchema>;

// Query schema for listing chats
export const chatQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type ChatQuery = z.infer<typeof chatQuerySchema>;

// Chat ID param schema
export const chatIdParamSchema = z.object({
  chatId: z.uuid('Invalid chat ID format'),
});
export type ChatIdParam = z.infer<typeof chatIdParamSchema>;

// ============================================
// SSE Event Types for Streaming Responses
// ============================================

export const sseThinkingEventSchema = z.object({
  type: z.literal('thinking'),
  content: z.string(),
});

export const sseToolStartEventSchema = z.object({
  type: z.literal('tool_start'),
  tool_name: z.string(),
  arguments: z.record(z.string(), z.unknown()),
});

export const sseToolResultEventSchema = z.object({
  type: z.literal('tool_result'),
  tool_name: z.string(),
  tool_result: toolResultSchema,
  formatted_text: z.string(),
});

export const sseResponseEventSchema = z.object({
  type: z.literal('response'),
  content: z.string(),
});

export const sseDoneEventSchema = z.object({
  type: z.literal('done'),
  chat_id: z.uuid(),
  tool_calls_executed: z.array(toolCallInfoSchema),
});

export const sseErrorEventSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
});

export const sseEventSchema = z.discriminatedUnion('type', [
  sseThinkingEventSchema,
  sseToolStartEventSchema,
  sseToolResultEventSchema,
  sseResponseEventSchema,
  sseDoneEventSchema,
  sseErrorEventSchema,
]);

export type SSEEvent = z.infer<typeof sseEventSchema>;
